/**
 * Redirection apres envoi d'un formulaire Contact Form 7, et reprise de
 * l'adresse email saisie sur la page de remerciement.
 *
 * Sert au suivi de conversion : la page de remerciement est une URL distincte,
 * sur laquelle GTM peut declencher une conversion Google Ads a l'affichage.
 *
 * IMPORTANT — nom de l'evenement. Contact Form 7 6.x a renomme ses evenements :
 * le statut `mail_sent` emet desormais `wpcf7sent`. L'ancien `wpcf7mailsent`,
 * encore recommande par la plupart des tutoriels, n'existe plus dans le script
 * du plugin (verifie dans includes/js/index.js de la version 6.1.7). On ecoute
 * les deux pour rester compatible si le plugin est retrograde, avec un verrou
 * pour ne jamais rediriger deux fois.
 *
 * L'URL est lue sur l'ancetre portant [data-cf7-redirect], pose par les layouts
 * « Hero avec formulaire » et « Contact – Formulaire » : un meme formulaire peut
 * donc pointer vers des pages differentes selon la page qui l'accueille.
 *
 * L'adresse saisie est ajoutee a l'URL de redirection (?email=...), pour etre
 * relue par la page de remerciement et par GTM. Elle est aussi conservee dans
 * sessionStorage, qui prend le relais si le parametre venait a etre retire de
 * l'URL (LiteSpeed sait supprimer certains parametres pour mutualiser son
 * cache : verifier que `email` n'est pas dans « Drop Query String »).
 */

const STORAGE_KEY = 'tizFormEmail';
const EMAIL_PARAM = 'email';
const MAX_AGE_MS = 30 * 60 * 1000; // au-dela, on considere la saisie perimee
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let redirecting = false;

/** Retrouve l'adresse saisie, quel que soit le nom donne au champ dans CF7. */
function extractEmail(event, form) {
  const inputs = (event.detail && event.detail.inputs) || [];
  const named = inputs.find((i) => /e?mail/i.test(i.name || '') && EMAIL_RE.test(i.value || ''));
  if (named) return named.value.trim();

  const any = inputs.find((i) => EMAIL_RE.test(i.value || ''));
  if (any) return any.value.trim();

  // Repli sur le DOM si l'evenement ne porte pas les valeurs.
  const field = form && form.querySelector('input[type="email"]');
  return field && EMAIL_RE.test(field.value) ? field.value.trim() : '';
}

function remember(email) {
  if (!email) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ email, at: Date.now() }));
  } catch (e) {
    // navigation privee ou stockage refuse : la page de remerciement affichera
    // simplement son texte de repli.
  }
}

function fromUrl() {
  try {
    const value = new URL(window.location.href).searchParams.get(EMAIL_PARAM);
    return value && EMAIL_RE.test(value.trim()) ? value.trim() : '';
  } catch (e) {
    return '';
  }
}

function recall() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return '';
    const { email, at } = JSON.parse(raw);
    if (!email || !EMAIL_RE.test(email)) return '';
    if (!at || Date.now() - at > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return '';
    }
    return email;
  } catch (e) {
    return '';
  }
}

/**
 * Ajoute ?email=... a l'URL de redirection sans casser la query string ni
 * l'ancre eventuellement deja presentes sur l'URL saisie en back-office.
 */
function withEmail(url, email) {
  if (!email) return url;
  try {
    const next = new URL(url, window.location.href);
    next.searchParams.set(EMAIL_PARAM, email);
    return next.toString();
  } catch (e) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${EMAIL_PARAM}=${encodeURIComponent(email)}`;
  }
}

export function initFormRedirect() {
  const handleSent = (event) => {
    if (redirecting) return;

    const origin = event.target;
    if (!origin || typeof origin.closest !== 'function') return;

    const email = extractEmail(event, origin.closest('form'));
    remember(email);

    // Expose la conversion a GTM (utile pour les conversions ameliorees
    // Google Ads, qui hachent l'adresse cote Google).
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'tiz_form_sent', formEmail: email || undefined });

    const holder = origin.closest('[data-cf7-redirect]');
    if (!holder) return;

    const url = holder.getAttribute('data-cf7-redirect');
    if (!url) return;

    redirecting = true;
    window.location.assign(withEmail(url, email));
  };

  document.addEventListener('wpcf7sent', handleSent); // CF7 6.x
  document.addEventListener('wpcf7mailsent', handleSent); // CF7 5.x
}

/**
 * Remplit les emplacements [tiz_form_email] de la page de remerciement, et
 * revele les blocs [tiz_form_email_if] uniquement si une adresse est connue.
 * L'adresse est lue dans l'URL (?email=...), avec sessionStorage en secours.
 */
export function initFormThanks(scope = document) {
  const slots = scope.querySelectorAll('[data-tiz-form-email]');
  const blocks = scope.querySelectorAll('[data-tiz-form-email-if]');
  if (!slots.length && !blocks.length) return;

  // Volontairement autoritaire : on reecrit meme quand aucune adresse n'est
  // connue. Si LiteSpeed a servi une copie en cache de la page, elle porte
  // l'adresse d'un autre envoi, qu'il faut effacer.
  const email = fromUrl() || recall();

  slots.forEach((el) => {
    el.textContent = email || el.getAttribute('data-tiz-form-email-fallback') || '';
  });
  blocks.forEach((el) => {
    el.hidden = !email;
  });
}
